/** @jsxImportSource solid-js */
import { customElement, noShadowDOM } from "solid-element";
import { createSignal } from "solid-js";
import { PrimaryButton } from "#palette/components/button.solid";
import { ErrorCallout } from "#palette/components/callout.solid";
import { createPasswordInput } from "#palette/components/form/password.solid";
import { createTextInput } from "#palette/components/form/text.solid";
import { Spinner } from "#palette/components/spinner.solid";
import { PublicRoutes } from "../src/config/routes";
import { globalState } from "../state";

export function LoginNewExperience(props: { onFinish: () => void }) {
  const email = createTextInput({
    placeholder: "Enter your username",
  });
  const password = createPasswordInput({
    label: "",
    placeholder: "Enter your password",
  });
  const [errorState, setErrorState] = createSignal(false);
  const [loading, setLoading] = createSignal(false);

  function tryLogin() {
    setLoading(true);
    setTimeout(() => {
      setErrorState(false);
      setLoading(true);

      // Wait a second to allow the UI to show the loader, since `client.login()` will block the main thread.
      setTimeout(() => {
        globalState.client
          ?.login(email.value(), password.value())
          .then(() => {
            props.onFinish();
          })
          .catch((e) => {
            setErrorState(true);
            throw e; // Re-throw the error so other monitoring tools can catch it.
          })
          .finally(() => {
            setLoading(false);
          });
      }, 100);
    }, 100);
  }

  return (
    <div class="w-full max-w-lg mx-auto grid grid-cols-1 items-center h-full mx-2">
      <ErrorCallout title="We're sad to share that Callisto is closing, and as of today, we are no longer accepting new accounts.">
        <p class="pt-2">
          If you already have an account, nothing changes right now. You can
          still access your incident log, download your information, or delete
          your account at any time.
        </p>
        <p>
          We are working to find a partner to continue operating this
          technology. If we succeed, we'll let you know what that means for your
          account. If we don't, we'll reach out before anything changes so you
          have time to download your information or delete your account, if you
          choose.
        </p>
        <p>
          <a
            href="https://www.projectcallisto.org/blog/closing"
            class="underline"
          >
            Read our full announcement and FAQs here.
          </a>
        </p>
      </ErrorCallout>
      <div>
        <div class="relative w-full mx-auto overflow-hidden bg-white rounded-lg shadow-md items-center">
          <div class="p-4">
            <h3 class="mt-3 text-xl font-medium text-center text-brand-900 font-serif">
              Login to Callisto Vault
            </h3>

            {errorState() && (
              <ErrorCallout title="We couldn't log you in.">
                <p class="text-sm">
                  Please check the username and password you provided.
                </p>
              </ErrorCallout>
            )}

            <form>
              <div class="mt-4">{email.component}</div>

              <div class="mt-4">{password.component}</div>

              <div class="flex items-center justify-between mt-4">
                <a
                  href={PublicRoutes.REQUEST_ACCOUNT_RECOVERY}
                  class="text-sm text-status-error-fg font-bold"
                >
                  Forgot your password?
                </a>

                <span>
                  <PrimaryButton
                    class="place-self-end"
                    onClick={(e) => {
                      e.preventDefault();
                      tryLogin();
                    }}
                  >
                    Login
                  </PrimaryButton>
                </span>
              </div>
            </form>
            {loading() && <Spinner />}
          </div>
        </div>
        <div class="text-center py-5 flex flex-col gap-2">
          <a href="/feedback" class="font-bold">
            Leave Feedback or Report an Issue
          </a>
          <a
            href="https://www.callistocampus.org/"
            target="_blank"
            rel="noopener"
          >
            Trying to access your Callisto Campus record?
          </a>
          <a
            href="https://projectcallisto.org/callisto-vault-privacy-policy"
            target="_blank"
            rel="noopener"
          >
            Privacy Policy
          </a>
          <a
            href="https://projectcallisto.org/terms-of-service"
            target="_blank"
            rel="noopener"
          >
            Terms of Service
          </a>
        </div>
      </div>
    </div>
  );
}

// This shim exists for Solid-in-React support as I port things.
// TODO: remove this.
customElement("login-new-experience", { finish: () => {} }, (props) => {
  noShadowDOM();

  return <LoginNewExperience onFinish={props.finish} />;
});
